import React, { useEffect, useRef, useState } from 'react';
import { OutputNode } from 'tone';
import { drawMixerAudioMeterEmpty } from '../../render/canvasRenderer';

interface VolumeMeterProps {
    audioNode?: OutputNode;
}

export const VolumeMeter: React.FC<VolumeMeterProps> = ({ audioNode }) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const rafId = useRef<number | null>(null);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const canvasWidth = 10;
    const canvasHeight = 130;

    useEffect(() => {
        const audioAvailable = !!audioNode && !!audioNode.context;

        if (!audioAvailable) {
            if (canvasRef.current) {
                const ctx = canvasRef.current.getContext('2d');
                if (!ctx) return;
                drawMixerAudioMeterEmpty(ctx, canvasWidth, canvasHeight);
            }
            return;
        }

        const analyser = audioNode.context.createAnalyser();
        analyser.fftSize = 256;
        audioNode.connect(analyser);
        analyserRef.current = analyser;
        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Float32Array(bufferLength);

        const updateVolume = () => {
            if (analyserRef.current) {
                analyserRef.current.getFloatTimeDomainData(dataArray);
                let sum = 0;
                for (let i = 0; i < dataArray.length; i++) {
                    sum += Math.pow(dataArray[i], 2);
                }
                const rms = Math.sqrt(sum / dataArray.length);

                // Draw the volume meter in the canvas
                if (canvasRef.current) {
                    const ctx = canvasRef.current.getContext('2d');
                    if (ctx) {
                        drawMixerAudioMeterEmpty(ctx, canvasWidth, canvasHeight);

                        // Calculate the bar height as a percentage (like your original code)
                        const barHeightPercentage = Math.min(100, rms * 1000);
                        const barHeightPixels = (barHeightPercentage / 100) * canvasHeight;

                        // Draw the meter bar (green rectangle) from the bottom up
                        const t = barHeightPercentage / 100; // 0 (green) -> 1 (red)
                        const climbPoint = 0.7;
                        const hue = (120 * climbPoint) + ((120 * climbPoint) * (1 - (2 * t))); // 120=green, 60=yellow, 0=red
                        ctx.fillStyle = `hsl(${hue} 100% 50%)`;
                        ctx.fillRect(0, canvasHeight - barHeightPixels, canvasWidth, barHeightPixels);
                    }
                }
            }
            rafId.current = requestAnimationFrame(updateVolume);
        };

        updateVolume();

        return () => {
            if (rafId.current) {
                cancelAnimationFrame(rafId.current);
            }
            try {
                audioNode.disconnect(analyser);
            } catch (e) {
                console.error('Error disconnecting analyser', e);
            }
        };
    }, [audioNode, canvasWidth, canvasHeight]);

    return (
        <canvas
            ref={canvasRef}
            width={canvasWidth}
            height={canvasHeight}
            style={{ border: '1px solid #000', display: 'block' }}
        />
    );
};
